/**
 * Local-timezone date helpers.
 *
 * Use these instead of `new Date().toISOString().split('T')[0]` — the latter
 * returns UTC, which rolls forward in the evening for users in the Americas
 * and causes "today" to display as tomorrow.
 *
 * COMPANY TIMEZONE (Eastern Time) helpers
 * ---------------------------------------
 * CLL operates out of Charlotte, NC — every user-facing timestamp in the
 * app (forms, PDFs, signatures, logs) MUST render in Eastern Time
 * regardless of the browser's local TZ (some users travel, some laptops
 * are on UTC). DST (EST ↔ EDT) is handled automatically by `Intl` when
 * `timeZone: 'America/New_York'` is passed. Use the `*ET` helpers for
 * all display and time-input defaults; use `localDateString` etc only
 * for private user-machine flows.
 */

export const COMPANY_TZ = 'America/New_York';

/** Format any date-ish value as `MM/DD/YYYY hh:mm A ET` (Eastern Time).
 *  Returns `—` for null/invalid inputs so callers never render "Invalid Date".
 */
export function formatDateTimeET(value) {
  const d = _coerceDate(value);
  if (!d) return '—';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: COMPANY_TZ,
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }).formatToParts(d);
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return `${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute} ${p.dayPeriod} ET`;
}

/** Format any date-ish value as `MM/DD/YYYY` in Eastern Time. */
export function formatDateET(value) {
  const d = _coerceDate(value);
  if (!d) return '—';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: COMPANY_TZ,
    month: '2-digit', day: '2-digit', year: 'numeric',
  }).formatToParts(d);
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return `${p.month}/${p.day}/${p.year}`;
}

/** Today's date in Eastern Time as `YYYY-MM-DD` (safe for `<input type="date">`). */
export function todayET() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: COMPANY_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  // en-CA gives ISO-ish parts; assemble YYYY-MM-DD.
  return `${p.year}-${p.month}-${p.day}`;
}

/** Any date-ish value's calendar date in Eastern Time as `YYYY-MM-DD`
 *  (safe for `<input type="date">`). Same idea as `todayET()` but for an
 *  arbitrary timestamp — e.g. converting an asset transaction's UTC
 *  `created_at` into the Date field of the Edit Maintenance Form dialog
 *  without it rolling to the wrong day near midnight ET, or being
 *  rejected outright by the native date input for not being plain
 *  `YYYY-MM-DD` (a full ISO datetime string renders blank). Returns ''
 *  for null/invalid input so the field just opens empty, not "Invalid Date".
 */
export function dateInputValueET(value) {
  const d = _coerceDate(value);
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: COMPANY_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

/** Current time in Eastern Time as `HH:MM` (24h — safe for `<input type="time">`). */
export function nowTimeET() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: COMPANY_TZ,
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return `${p.hour}:${p.minute}`;
}

function _coerceDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Today's date in the user's local timezone, formatted as `YYYY-MM-DD`. */
export function todayLocal() {
  const d = new Date();
  return localDateString(d);
}

/** Format any Date object as `YYYY-MM-DD` in local time. */
export function localDateString(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** N days from now (positive = future, negative = past), `YYYY-MM-DD` local time. */
export function localDateOffset(daysOffset) {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  return localDateString(d);
}
